import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';

// Bar charts and median deltas for the chosen peer groups (from getAnalysisPeerComparison)
export default function BenchmarkComparison({ analysisData, peers }) {
  const { category_scores } = analysisData;
  const groups = [peers?.industry, peers?.index].filter((group) => group?.overall);

  if (groups.length === 0) {
    return null;
  }

  // The bars show each group's mean, so they are labelled "average"
  const averageKey = (group) => `${group.label} average`;
  const userScore = groups[0].overall.user_pct;

  // Prepare data for overall score comparison
  const overallData = [{
    category: 'Overall Score',
    'Your Company': userScore,
    ...Object.fromEntries(groups.map((group) => [averageKey(group), group.overall.mean_pct])),
  }];

  // Prepare data for category comparison
  const categoryData = Object.entries(category_scores ?? {}).map(([category, score]) => ({
    category,
    'Your Company': score,
    ...Object.fromEntries(groups
      .filter((group) => group.category_means?.[category] != null)
      .map((group) => [averageKey(group), group.category_means[category]])),
  }));

  // Your brand colors
  const colors = {
    yourCompany: '#1f4c54', // var(--accent)
    industry: '#a3a3a3',    // neutral gray
    index: '#d4d4d4'        // lighter gray
  };
  const groupColor = (group) => (group.kind === 'index' ? colors.index : colors.industry);

  const legendText = (value) => <span style={{ color: 'var(--text-secondary)' }}>{value}</span>;

  // Custom tooltip with your styling
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-white/95 p-3 shadow-lg backdrop-blur">
          <p className="text-xs font-semibold text-[var(--text-primary)]">{payload[0].payload.category}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mt-1 text-xs text-[var(--text-secondary)]">
              <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} aria-hidden="true" />
              <span className="font-medium text-[var(--text-primary)]">{entry.name}</span>: {entry.value.toFixed(1)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className="space-y-8 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/90 p-8 shadow-[0_28px_56px_rgba(15,23,42,0.08)]"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="space-y-2">
        <p className="eyebrow-muted">
          Detailed comparison
        </p>
        <h2 className="font-display text-2xl font-semibold !tracking-display text-[var(--text-primary)]">
          Benchmark analysis
        </h2>
      </div>

      {/* Overall Score Comparison */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-[var(--text-primary)]">
          Overall score comparison
        </h3>
        <div className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-[var(--surface-ambient)]/50 p-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={overallData} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(31,76,84,0.1)" />
              <XAxis
                dataKey="category"
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(31,76,84,0.2)' }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(31,76,84,0.2)' }}
                label={{ value: 'Score (%)', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)', fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                iconType="circle"
                formatter={legendText}
              />

              <Bar dataKey="Your Company" fill={colors.yourCompany} radius={[8, 8, 0, 0]} />
              {groups.map((group) => (
                <Bar key={group.key} dataKey={averageKey(group)} fill={groupColor(group)} radius={[8, 8, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Scores Comparison */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-[var(--text-primary)]">
          Category breakdown
        </h3>
        <div className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-[var(--surface-ambient)]/50 p-4">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={categoryData} margin={{ top: 10, right: 10, left: -20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(31,76,84,0.1)" />
              <XAxis
                dataKey="category"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(31,76,84,0.2)' }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(31,76,84,0.2)' }}
                label={{ value: 'Score (%)', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)', fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                iconType="circle"
                formatter={legendText}
              />

              <Bar dataKey="Your Company" fill={colors.yourCompany} radius={[8, 8, 0, 0]} />
              {groups.map((group) => (
                <Bar key={group.key} dataKey={averageKey(group)} fill={groupColor(group)} radius={[8, 8, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {groups.map((group) => (
          <MedianDeltaCard key={group.key} group={group} />
        ))}
      </div>
    </motion.div>
  );
}

function MedianDeltaCard({ group }) {
  const { overall } = group;
  const atMedian = overall.position === 'at_median';
  const delta = overall.user_pct - overall.median_pct;

  return (
    <div className="rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white/80 p-6 shadow-[0_8px_16px_rgba(15,23,42,0.06)]">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="eyebrow-muted">
            vs {group.label} median
          </p>
          <p className="text-2xl font-semibold text-[var(--text-primary)]">
            {atMedian ? 'At the median' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)} pts`}
          </p>
        </div>
        {delta >= 0 ? (
          <TrendingUp className="h-5 w-5 text-success-700" />
        ) : (
          <TrendingDown className="h-5 w-5 text-danger-700" />
        )}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
        Your company scores {atMedian ? 'at' : (delta > 0 ? 'above' : 'below')} the {group.label} median of {overall.median_pct.toFixed(1)}%
      </p>
    </div>
  );
}
